import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PHONE_QUERY, useMediaQuery } from "@/hooks/use-media-query";

export type BackTarget = { to: string; label: string };

// Where each page's back arrow leads. Deliberately a fixed parent ("up" one
// level in the app's hierarchy), not browser history - history-back would
// send someone who just submitted a request straight back into the wizard,
// and does nothing useful on a page opened from a bookmark or a fresh tab.
// Browser Back still works as usual alongside it. The one exception is a
// page with a `step` query param (see previousStep below).
// Checked in order, first prefix match wins; anything unlisted (every
// top-level page) goes back to the Dashboard.
const PARENTS: { prefix: string; target: BackTarget }[] = [
  { prefix: "/my-requests/new", target: { to: "/my-requests", label: "Travel Reimbursements" } },
  { prefix: "/other-reimbursements/new", target: { to: "/other-reimbursements", label: "Other Reimbursements" } },
];

const DASHBOARD: BackTarget = { to: "/", label: "Dashboard" };

/** A page that keeps a multi-step flow in a `step` query param (Policies'
 *  limits flows) is stepped back one step first - from step 1 back to the
 *  page's own overview (no `step`), and only from there "up" to the
 *  page's parent. Every other query param is kept, so the previous step
 *  reopens with the same selections. */
function previousStep(pathname: string, search: string): BackTarget | null {
  const params = new URLSearchParams(search);
  const step = Number(params.get("step"));
  if (!Number.isInteger(step) || step < 1) return null;
  if (step === 1) params.delete("step");
  else params.set("step", String(step - 1));
  const qs = params.toString();
  return { to: qs ? `${pathname}?${qs}` : pathname, label: "previous step" };
}

export function backTargetFor(pathname: string, search = ""): BackTarget | null {
  if (pathname === "/") return null;
  return previousStep(pathname, search) ?? PARENTS.find((p) => pathname.startsWith(p.prefix))?.target ?? DASHBOARD;
}

/** The current route's back target, or null on the Dashboard (the root). */
export function useBackTarget(): BackTarget | null {
  const { pathname, search } = useLocation();
  return backTargetFor(pathname, search);
}

// Marks an extra history entry pushed by the back-stack below - lets its
// popstate handler tell "a pop caused by one of our own guard entries" apart
// from any other state shape (there shouldn't be another "page" entry pushed
// on top of it, but this is a cheap, explicit check rather than assuming it).
const BACK_GUARD_STATE = { asmaraBackGuard: true } as const;

// A stack of phone-only "things a back gesture should dismiss" - the mobile
// sidebar drawer today, and a natural place for a future detail dialog/sheet
// to register itself too (see useBackClosesOnGesture below). Only the
// TOP-most handler runs per gesture, same as a real native back stack: an
// open drawer should swallow the gesture entirely, not also bump the page
// back to its parent in the same tap. useHardwareBackNavigation registers
// itself as the permanent bottom-of-stack entry (page-level "go to parent"),
// so there's always at least one handler whenever something has pushed a
// guard entry onto real browser history.
type BackHandler = { run: () => void; consumedByPop: boolean };
const backHandlerStack: BackHandler[] = [];

function pushBackGuard(): void {
  window.history.pushState(BACK_GUARD_STATE, "", window.location.href);
}

// Set right before useBackStackEntry's cleanup calls history.back() to
// consume its own unconsumed guard entry (see that function's docstring) -
// the resulting popstate is this module's own bookkeeping, not a real back
// gesture, so the shared listener below must ignore exactly that one event
// instead of letting it fall through to whatever handler is now on top.
let selfInflictedPops = 0;

/** Registers `onBack` to run the next time a back gesture pops a guard
 *  entry while `active` is true, ahead of any handler registered earlier
 *  (LIFO - last pushed, first to handle a gesture). Pushes one guard entry
 *  right away so there's always something for the very next gesture to pop.
 *  Phone-only (`isPhone` gates whether this does anything at all) - desktop/
 *  tablet back stays real browser history, same reasoning as
 *  useHardwareBackNavigation below.
 *
 *  If `active` turns false WITHOUT a back gesture (e.g. the overlay this
 *  backs was closed by tapping outside it, not by the back button), this
 *  hook's own guard entry is still sitting unconsumed in browser history -
 *  left alone, the NEXT unrelated back gesture would silently pop that
 *  stale entry and do nothing, requiring a second press. `history.back()`
 *  on cleanup consumes it itself in that case (skipped when the gesture
 *  already consumed it - see onBack below - so a real back press doesn't
 *  get doubled up).
 *
 *  Exported (not just used internally by useBackClosesOnGesture) for a
 *  layered overlay that needs its OWN back-gesture handling smarter than a
 *  plain "close" - e.g. CameraCapture's camera/preview layers, where back
 *  on the camera layer asks to discard pending photos instead of closing
 *  outright. Every such layer MUST go through this (or useBackClosesOnGesture)
 *  rather than pushing its own raw history.pushState/popstate pair - this
 *  module owns the one shared `window` popstate listener (see
 *  useHardwareBackNavigation), and a second, independent listener racing it
 *  for the same browser-level events is exactly the bug this was written to
 *  prevent (two uncoordinated handlers each treating the other's popstate as
 *  a real gesture). */
export function useBackStackEntry(active: boolean, isPhone: boolean, onBack: BackHandler["run"]): void {
  const onBackRef = React.useRef(onBack);
  onBackRef.current = onBack;

  React.useEffect(() => {
    if (!isPhone || !active) return;
    const handler: BackHandler = {
      run: () => onBackRef.current(),
      consumedByPop: false
    };
    backHandlerStack.push(handler);
    pushBackGuard();
    return () => {
      const i = backHandlerStack.lastIndexOf(handler);
      if (i !== -1) backHandlerStack.splice(i, 1);
      if (!handler.consumedByPop) {
        selfInflictedPops++;
        window.history.back();
      }
    };
  }, [isPhone, active]);
}

/** Phone-only: makes a back gesture close an open overlay (the mobile
 *  sidebar drawer today) instead of navigating the page - the same "closest
 *  thing first" order a native app's back button follows. `open` is the
 *  overlay's own existing open/close state (e.g. SidebarProvider's
 *  `openMobile`); `onClose` should just close it (e.g. `setOpenMobile
 *  (false)`) - this hook handles not letting the gesture also fall through
 *  to page navigation underneath. */
export function useBackClosesOnGesture(open: boolean, onClose: () => void): void {
  const isPhone = useMediaQuery(PHONE_QUERY);
  useBackStackEntry(open, isPhone, onClose);
}

/** Call right before closing an overlay registered via useBackClosesOnGesture
 *  for a reason OTHER than a back gesture or a tap-outside-to-dismiss -
 *  specifically, navigating to another route from a Link *inside* the
 *  overlay (e.g. the mobile sidebar drawer's own nav items). That kind of
 *  close is about to push its own new history entry for the destination
 *  route (the Link's navigation runs right after this, synchronously -
 *  react-router calls our onClick before its own internal handler); left
 *  alone, useBackStackEntry's cleanup would see the close as "not consumed
 *  by a pop" and call `history.back()` AFTER that new entry exists, popping
 *  it instead of the stale guard underneath and silently cancelling the
 *  navigation (`history.back()` queues an async traversal rather than
 *  acting immediately, so it can never reliably win a race against a
 *  synchronous `pushState()` the Link's own navigate() is about to make -
 *  the traversal is specified to run, and can be pre-empted, on a later
 *  task, by which point that pushState has already moved the current
 *  entry). Marking the handler `consumedByPop` here - WITHOUT touching
 *  browser history - makes that cleanup's `history.back()` a no-op, the
 *  same as a real back gesture consuming it, so the Link's own history
 *  push lands normally. The guard entry itself is simply left behind,
 *  inert, underneath the new page entry - harmless: useHardwareBackNavigation
 *  pushes a fresh guard for the new route right away, so an ordinary
 *  one-entry-at-a-time back gesture never reaches the stale one (only a
 *  multi-entry jump could skip past the new guard onto it, which does
 *  nothing worse than a single wasted gesture). */
export function consumeBackGuardForNavigation(): void {
  const top = backHandlerStack[backHandlerStack.length - 1];
  if (!top) return;
  top.consumedByPop = true;
}

/** Phone-only: makes the hardware back button / edge-swipe-back gesture
 *  behave like a native app's back action (go to this route's logical
 *  parent - same destination as the on-screen back arrow) instead of
 *  replaying raw browser history, which could land anywhere (including back
 *  INTO a flow like the request wizard that the person already left).
 *
 *  How: on every route change this pushes one extra ("guard") history entry
 *  on top of the real one for that route. A back gesture pops that guard
 *  entry first, firing `popstate` - which this hook catches, immediately
 *  re-pushes the guard (so the browser's own history position doesn't
 *  actually move) and calls `navigate(backTarget.to)` instead. Going through
 *  `navigate()` (not a raw history rewrite) keeps this a normal router
 *  transition, so NewRequest.tsx's `useBlocker` unsaved-bills guard still
 *  gets a chance to intercept it exactly as it does a Link click.
 *
 *  Registers itself as the permanent bottom of the shared back-handler stack
 *  (see above) - an open overlay registered via useBackClosesOnGesture
 *  handles the gesture first instead, same as a native app dismissing its
 *  topmost sheet before ever reaching the page underneath.
 *
 *  Desktop/tablet are unaffected - real browser Back there is unsurprising
 *  (no gesture, a visible address bar, usually a mouse), and this hook
 *  would otherwise make the browser's own Back button in BrowserChrome feel
 *  broken by refusing to leave the app's own history at all. */
export function useHardwareBackNavigation(): void {
  const isPhone = useMediaQuery(PHONE_QUERY);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  // Kept in a ref so the popstate handler (registered once) always reads
  // the CURRENT back target without needing to re-subscribe on every route
  // change - re-subscribing on each navigation risks a race where a guard
  // entry's popstate fires before the new handler is wired up.
  const backTargetRef = React.useRef<BackTarget | null>(null);
  backTargetRef.current = backTargetFor(pathname, search);

  const navigateToParent = React.useCallback(() => {
    const target = backTargetRef.current;
    // No parent (the Dashboard, the app's root) - nothing to redirect to,
    // so let the gesture through instead of re-arming the guard. Re-arming
    // unconditionally would trap the person on the Dashboard with no way
    // for a back gesture to ever leave the app (close the PWA/tab, return
    // to whatever page was open before it).
    if (!target) return;
    // Re-arm the guard immediately - this keeps the browser's history
    // position pinned at the current page instead of actually moving back,
    // so the only visible effect of the gesture is the navigate() below (or
    // nothing, if a blocker cancels it).
    pushBackGuard();
    navigate(target.to);
  }, [navigate]);

  // Always active (unlike useBackClosesOnGesture's overlays) - this is the
  // stack's permanent floor, pushed once on mount/phone-change and never
  // removed for the lifetime of AppShell.
  useBackStackEntry(true, isPhone, navigateToParent);

  // Re-push a fresh guard on every route change too (not just on mount) -
  // otherwise a guard popped by navigateToParent's own navigate() call
  // above would be the route's only guard, and useBackStackEntry's
  // mount-time push wouldn't run again for the new route.
  React.useEffect(() => {
    if (!isPhone) return;
    pushBackGuard();
  }, [isPhone, pathname, search]);

  React.useEffect(() => {
    if (!isPhone) return;
    const onPopState = () => {
      // This pop is useBackStackEntry's own cleanup consuming an entry an
      // overlay left behind when it closed WITHOUT a back gesture (tapped
      // outside, a nav Link, etc.) - not a real gesture, so it must not
      // also dispatch to whatever handler is now on top (see that
      // function's docstring for the scenario this prevents).
      if (selfInflictedPops > 0) {
        selfInflictedPops--;
        return;
      }
      // Topmost registered handler wins - an open drawer (or, later, a
      // dialog) dismisses itself instead of also falling through to page
      // navigation in the same gesture.
      const top = backHandlerStack[backHandlerStack.length - 1];
      if (!top) return;
      // Marked BEFORE running it - the handler's own cleanup (e.g. the
      // drawer's onClose flipping `open` to false, tearing down its
      // useBackStackEntry) checks this synchronously to skip the extra
      // history.back() it'd otherwise issue for an entry this gesture
      // already consumed.
      top.consumedByPop = true;
      top.run();
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [isPhone]);
}
